import React from 'react';
import { useNavigate } from 'react-router-dom';
import { IconBell, IconCircle, IconCircleFilled } from '@tabler/icons-react';
import { Indicator } from '@mantine/core';
import { Box } from 'src/design/atoms/Box';
import { Divider } from 'src/design/atoms/Divider';
import { Group } from 'src/design/atoms/Group';
import { Loader } from 'src/design/atoms/Loader';
import { NavLink } from 'src/design/atoms/NavLink';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { Tooltip } from 'src/design/atoms/Tooltip';
import { BasicActionIcon } from 'src/design/atoms/BasicActionIcon';
import { BasicButton } from 'src/design/atoms/BasicButton';
import { Drawer } from 'src/design/molecules/Drawer';
import moment from 'moment';
import { formatDateTime } from 'src/utils';
import { useUserNotifications } from 'src/api/queries/userNotifications/useUserNotifications';
import { useMarkNotificationRead } from 'src/api/queries/userNotifications/useUserNotificationMutations';

const DrawerWidth = 350;

/** @registry Header notification bell: unread-count indicator, drawer listing all/unread notifications with per-item mark-read. */
export const Notifications: React.FC = () => {
  const navigate = useNavigate();
  const [tab, setTab] = React.useState('all');
  const [open, setOpen] = React.useState(false);

  const { data, isLoading: loading } = useUserNotifications({
    unread: tab === 'unread',
  });
  const notifications = React.useMemo(() => data ?? [], [data]);
  const unreadCount = notifications.filter((n) => !n.read).length;

  const markRead = useMarkNotificationRead();

  const renderNotifications = () => {
    if (notifications.length === 0) {
      return (
        <Text ml="md" mt="sm">
          No Notifications
        </Text>
      );
    }
    return notifications.map((notification) => {
      const createdDate = moment(notification.createdAt);
      const timeago = moment().diff(createdDate, 'days') <= 1;
      return (
        <React.Fragment key={notification.id}>
          <NavLink
            onClick={() => {
              setOpen(false);
              navigate(
                `/jobs/${notification.jobId}/activity?postId=${notification.postId}`,
              );
            }}
            label={
              <Stack gap={2}>
                <Text size="sm">
                  <Text component="span" fw={500}>
                    {notification.person?.fullName}{' '}
                  </Text>
                  {notification.message}{' '}
                  <Text component="span" fw={500}>
                    in {notification.job?.name}
                  </Text>
                </Text>
                <Text size="xs" c={notification.read ? 'dimmed' : 'blue'}>
                  {timeago
                    ? createdDate.fromNow()
                    : formatDateTime(
                        notification.createdAt,
                        'MM/DD/YYYY [at] h:mm a',
                      )}
                </Text>
              </Stack>
            }
            rightSection={
              notification.read ? (
                <Tooltip label="Read">
                  <IconCircle size={16} style={{ cursor: 'pointer' }} />
                </Tooltip>
              ) : (
                <Tooltip label="Unread">
                  <IconCircleFilled
                    size={16}
                    color="var(--mantine-color-blue-filled)"
                    style={{ cursor: 'pointer' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      markRead.mutate(notification.id);
                    }}
                  />
                </Tooltip>
              )
            }
          />
          <Divider />
        </React.Fragment>
      );
    });
  };

  return (
    <>
      <Box style={{ flexShrink: 0, marginLeft: 6 }}>
        <Indicator
          label={unreadCount > 0 ? unreadCount : undefined}
          size={16}
          disabled={unreadCount === 0}
        >
          <BasicActionIcon
            variant="subtle"
            color="dark"
            size="lg"
            aria-label="open notifications"
            onClick={() => setOpen((prevState) => !prevState)}
          >
            <IconBell size={20} />
          </BasicActionIcon>
        </Indicator>
      </Box>
      <Drawer
        opened={open}
        onClose={() => setOpen(false)}
        position="right"
        size={DrawerWidth}
        title="Notifications"
        styles={{ body: { padding: 0 } }}
      >
        <Box p="sm">
          <Group gap="xs" mb="sm">
            <BasicButton
              variant={tab === 'all' ? 'filled' : 'subtle'}
              size="xs"
              onClick={() => setTab('all')}
            >
              All
            </BasicButton>
            <BasicButton
              variant={tab === 'unread' ? 'filled' : 'subtle'}
              size="xs"
              onClick={() => setTab('unread')}
            >
              Unread
            </BasicButton>
          </Group>
        </Box>
        {loading ? (
          <Box style={{ textAlign: 'center', paddingTop: 50 }}>
            <Loader />
          </Box>
        ) : (
          <Stack gap={0}>{renderNotifications()}</Stack>
        )}
      </Drawer>
    </>
  );
};
